import {
    ArrowRight,
    Database,
    Plug,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { mcpConfig } from "../../lib/mcp-config";

// // // //

const FEATURES = [
    {
        icon: Database,
        title: "Comprehensive RWA data",
        description:
            "Query tokenized asset metrics, issuers, and networks directly from your AI assistant.",
    },
    {
        icon: ShieldCheck,
        title: "Secure, revocable access",
        description:
            "Connect with OAuth or an API key. Your credentials stay private — revoke access anytime from Settings.",
    },
    {
        icon: Plug,
        title: "Works where you work",
        description:
            "Connect from Claude, Claude Code, Cursor, or Codex in minutes — no custom integration required.",
    },
] as const;

/**
 * McpSetupHero
 * Marketing-style hero for the MCP setup guide landing layout.
 */
export function McpSetupHero(props: { onConnectClick: () => void }) {
    const { onConnectClick } = props;

    return (
        <section className="flex flex-col justify-center lg:py-4">
            {/* Eyebrow */}
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-primary">
                <Sparkles className="size-3.5" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wide">
                    New
                </span>
            </div>

            {/* Headline + subhead */}
            <div className="mt-6 flex flex-col gap-4">
                <h2 className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                    Bring {mcpConfig.serverName} data into your AI workflow
                </h2>
                <p className="max-w-lg text-pretty text-base leading-relaxed text-secondary-foreground">
                    The {mcpConfig.serverName} MCP server gives your AI tools —
                    Claude, Claude Code, Cursor, and Codex — direct access to
                    tokenized real-world asset data, so you can research
                    markets, compare assets, and answer questions without
                    leaving your chat.
                </p>
            </div>

            {/* CTAs */}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button
                    type="button"
                    size="lg"
                    variant="primaryLight"
                    onClick={onConnectClick}
                >
                    Connect now
                    <ArrowRight className="size-4" aria-hidden="true" />
                </Button>
            </div>

            {/* Feature list */}
            <ul className="mt-5 flex flex-col gap-2">
                {FEATURES.map((feature) => (
                    <li key={feature.title} className="flex gap-4 p-3">
                        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <feature.icon
                                className="size-5"
                                aria-hidden="true"
                            />
                        </span>
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-foreground">
                                {feature.title}
                            </span>
                            <span className="text-sm leading-relaxed text-secondary-foreground">
                                {feature.description}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
